import {
  VEHICLES,
  VehicleId,
  getRouteKey,
  resolvePricingZone,
} from "@/config/infos";
import { POINTS_INTERET } from "@/components/forms/constants";

type TripType = "one-way" | "round-trip" | "multi-trip" | "disposition";

type Coordinates = { lat: number; lon: number };

export type PricingBreakdown = {
  distanceKm?: number;
  computedByKm: boolean;
  fixedTransferApplied: boolean;
  fixedTransferKey?: string;
  disposalPackageUsed?: {
    hours: number;
    price: number;
    extraHours: number;
    extraHourPrice: number;
  };
  pricePerKm?: number;
};

type StopInput = {
  address: string;
  waitingTime?: number;
};

export type ReservationPricingInput = {
  departure: string;
  arrival?: string;
  tripType: TripType;
  stops?: StopInput[];
  dispositionDuration?: number;
  selectedVehicleId?: VehicleId;
  passengers: number;
  luggage: number;
  babySeats: number;
  childSeats: number;
  acceptAnimals?: boolean;
};

export type ReservationPricingProviders = {
  resolveCoordinates: (address: string) => Promise<Coordinates | null>;
  resolveRouteDistance: (
    start: Coordinates,
    end: Coordinates,
  ) => Promise<number | null>;
};

export type ReservationPricingResult = {
  amount: number;
  priceBreakdown: PricingBreakdown;
  normalizedInput: {
    dispositionDuration?: number;
  };
  warnings: string[];
};

type PoiInfo = {
  id: string;
  pricingZoneId: string | null;
  coordinates: Coordinates | null;
};

const findPOIInfo = (address: string): PoiInfo | null => {
  const allPOIs = [
    ...POINTS_INTERET.gares,
    ...POINTS_INTERET.aeroports,
    ...POINTS_INTERET.destinations,
  ];

  let poi = allPOIs.find((p) => p.adresse === address || p.nom === address);
  if (!poi) {
    const addressLower = address.toLowerCase();
    poi = allPOIs.find((p) => {
      return (
        addressLower.includes(p.nom.toLowerCase()) ||
        p.nom.toLowerCase().includes(addressLower.split(",")[0].trim())
      );
    });
  }

  if (!poi) return null;
  return {
    id: poi.id,
    pricingZoneId: poi.pricingZoneId ?? null,
    coordinates: poi.coordinates ?? null,
  };
};

const resolvePricingZoneKey = async (
  address: string,
  providers: ReservationPricingProviders,
): Promise<string | null> => {
  const poi = findPOIInfo(address);
  if (poi) {
    const zone = resolvePricingZone({
      pricingZoneId: poi.pricingZoneId,
      coordinates: poi.coordinates,
    });
    return zone ?? poi.id;
  }

  const coords = await providers.resolveCoordinates(address);
  if (coords) {
    const zone = resolvePricingZone({ coordinates: coords });
    if (zone) return zone;
  }

  return null;
};

const requireAddress = (value: string | undefined, message: string): string => {
  const normalized = value?.trim();
  if (!normalized) {
    throw new Error(message);
  }
  return normalized;
};

const getCoordsOrThrow = async (
  address: string,
  providers: ReservationPricingProviders,
): Promise<Coordinates> => {
  const coords = await providers.resolveCoordinates(address);
  if (!coords) {
    throw new Error("Impossible de calculer la distance entre les points");
  }
  return coords;
};

const getRouteDistanceOrThrow = async (
  start: Coordinates,
  end: Coordinates,
  providers: ReservationPricingProviders,
): Promise<number> => {
  const routeDistance = await providers.resolveRouteDistance(start, end);
  if (!routeDistance) {
    throw new Error("Impossible de calculer la distance routiere");
  }
  return routeDistance;
};

export const estimateReservationPrice = async (
  input: ReservationPricingInput,
  providers: ReservationPricingProviders,
): Promise<ReservationPricingResult> => {
  const departure = requireAddress(
    input.departure,
    "Renseignez au minimum le depart pour estimer.",
  );
  const selectedVehicleId = input.selectedVehicleId ?? "CLASS_V";
  const selectedVehicle = VEHICLES[selectedVehicleId];
  const warnings: string[] = [];

  if (input.acceptAnimals) {
    throw new Error("Les animaux ne sont pas acceptes a bord.");
  }

  const totalPassengers = input.passengers + input.babySeats + input.childSeats;
  if (totalPassengers > selectedVehicle.maxPassengers) {
    throw new Error(
      `Le vehicule ${selectedVehicle.classLabel} ne peut accueillir que ${selectedVehicle.maxPassengers} passagers maximum`,
    );
  }
  if (input.luggage > selectedVehicle.maxLuggage) {
    throw new Error(
      `Le vehicule ${selectedVehicle.classLabel} ne peut transporter que ${selectedVehicle.maxLuggage} bagages maximum`,
    );
  }

  let totalPrice = 0;
  let normalizedInput: ReservationPricingResult["normalizedInput"] = {};
  let priceBreakdown: PricingBreakdown = {
    computedByKm: false,
    fixedTransferApplied: false,
  };

  if (input.tripType === "disposition") {
    if (!input.dispositionDuration) {
      throw new Error("Renseignez une duree pour la mise a disposition.");
    }
    let duration = Math.max(3, input.dispositionDuration);

    let selectedPackage = selectedVehicle.disposalPackages.find(
      (pkg) => pkg.hours === duration,
    );
    if (!selectedPackage) {
      const sortedPackages = [...selectedVehicle.disposalPackages].sort(
        (a, b) => a.hours - b.hours,
      );
      selectedPackage = sortedPackages.find((pkg) => pkg.hours <= duration);
      if (!selectedPackage) {
        selectedPackage = sortedPackages[0];
        duration = selectedPackage.hours;
      }
    }

    const extraHours = Math.max(0, duration - selectedPackage.hours);
    totalPrice = selectedPackage.price + extraHours * selectedPackage.extraHour;
    normalizedInput = { dispositionDuration: duration };
    priceBreakdown = {
      computedByKm: false,
      fixedTransferApplied: false,
      disposalPackageUsed: {
        hours: selectedPackage.hours,
        price: selectedPackage.price,
        extraHours,
        extraHourPrice: selectedPackage.extraHour,
      },
    };

    return {
      amount: Math.round(totalPrice),
      priceBreakdown,
      normalizedInput,
      warnings,
    };
  }

  const arrival = requireAddress(
    input.arrival,
    "Renseignez la destination pour estimer ce type de trajet.",
  );

  if (input.tripType === "one-way" || input.tripType === "round-trip") {
    const departureKey =
      (await resolvePricingZoneKey(departure, providers)) || departure;
    const arrivalKey =
      (await resolvePricingZoneKey(arrival, providers)) || arrival;
    const routeKey = getRouteKey(departureKey, arrivalKey);
    const fixedPrice = selectedVehicle.fixedTransfers[routeKey];

    if (fixedPrice !== undefined) {
      totalPrice = fixedPrice;
      priceBreakdown = {
        computedByKm: false,
        fixedTransferApplied: true,
        fixedTransferKey: routeKey,
      };
    } else {
      const [departCoords, arriveeCoords] = await Promise.all([
        getCoordsOrThrow(departure, providers),
        getCoordsOrThrow(arrival, providers),
      ]);
      const routeDistance = await getRouteDistanceOrThrow(
        departCoords,
        arriveeCoords,
        providers,
      );
      totalPrice = routeDistance * selectedVehicle.pricePerKm;
      priceBreakdown = {
        distanceKm: routeDistance,
        computedByKm: true,
        fixedTransferApplied: false,
        pricePerKm: selectedVehicle.pricePerKm,
      };
    }

    if (input.tripType === "round-trip") {
      totalPrice *= 2;
    }

    return {
      amount: Math.round(totalPrice),
      priceBreakdown,
      normalizedInput,
      warnings,
    };
  }

  const stopAddresses = (input.stops ?? [])
    .map((stop) => stop.address.trim())
    .filter(Boolean);
  const itinerary = [departure, ...stopAddresses, arrival];
  let totalDistance = 0;

  for (let i = 0; i < itinerary.length - 1; i++) {
    const [startCoords, endCoords] = await Promise.all([
      getCoordsOrThrow(itinerary[i], providers),
      getCoordsOrThrow(itinerary[i + 1], providers),
    ]);
    const segmentDistance = await getRouteDistanceOrThrow(
      startCoords,
      endCoords,
      providers,
    );
    totalDistance += segmentDistance;
  }

  totalPrice = totalDistance * selectedVehicle.pricePerKm;
  priceBreakdown = {
    distanceKm: totalDistance,
    computedByKm: true,
    fixedTransferApplied: false,
    pricePerKm: selectedVehicle.pricePerKm,
  };

  return {
    amount: Math.round(totalPrice),
    priceBreakdown,
    normalizedInput,
    warnings,
  };
};
